import React, { FC, useMemo } from 'react';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import AnomalyRecommendation from './anomaly-recommendation';
import Conditional from '../../common/conditional';
import CustomUTMBreakdown from '../custom-utm/custom-utm-breakdown';
import CustomClass from '../custom-general/custom-damage-class';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomPDFExport from '../../blocks/pdf-export';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomSection from '../custom-general/custom-section';
import ConcreteHammerPDF from '../custom-pcnl/concrete-hammer-template/report-pdf';
import CustomCoverPage from '../custom-general/custom-cover-page';
import ConcreteHammerSummary from '../custom-pcnl/concrete-hammer-summary/summary';
import CustomUTMReportPDF from '../custom-utm/custom-utm-report';
import ExecutiveSummary from './executive-summary';
import OverviewSheet from '../custom-general/overview-sheet';
import StructureAnalytics from './report-items/structureAnalytics';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';
import CustomVisualInspectionSummary from '../custom-utm/custom-utm-summary';

import config from '../../../utils/config';

interface Props {
	coverData: models.CustomFVIData;
	projectDetails: models.Project;
	structureItems: models.FVIStructureItem[];
	reportTitle: string;
	checklistItems: models.ChecklistItem[];
	additionalComment: string;
	executiveSummary: string;
	pidData: models.PIDData;
	includeSummary: models.IncludeSummary;
	generalPhotos: models.PDFImageProps[];
	anomalyLocationData: models.AnomalyLocationData;
	concreteHammerData: models.ConcreteHammerData;
	tmlLocations: models.PDFImageProps[];
	utInspectionItems: models.UTMInspection[];
	inspectionOverview: models.CustomUTMOverview;
	findingsDescription?: string;
	findingsLocation?: string;
	structuralOverview?: string;
	overviewComponents?: models.StructuralOverviewComponent[];
}

const NDTStructuralReport: FC<Props> = (props) => {
	const { inspectionTypeName } = props.projectDetails;
	
	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure (Structure).',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const executiveSummary = useMemo(() => {
		if (props.includeSummary === 'include') {
			return props.executiveSummary + utils.getExecutiveSummaryAffix(props.structureItems, 'structure member(s)');
		}

		return props.executiveSummary;
	}, [props.structureItems, props.includeSummary, props.executiveSummary]);

	const anomalyItems = useMemo(() => {
		return props.structureItems.flatMap((item) => item.anomalyItems);
	}, [props.structureItems]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);


	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<Conditional isVisible={!!config.enableNDTComponentInspectionDetails && !!(props.findingsDescription || props.findingsLocation)}>
				<CustomSection sectionTitle="Inspection Findings" sectionSubtitle={props.findingsLocation}>
					<div className="p-2">{props.findingsDescription}</div>
				</CustomSection>
			</Conditional>

			<Conditional isVisible={!!config.enableNDTComponentInspectionDetails && !!(props.structuralOverview || (props.overviewComponents && props.overviewComponents.length > 0))}>
				<CustomSection sectionTitle="Structural Overview">
					<div className="p-2">{props.structuralOverview}</div>

					{(props.overviewComponents ?? []).map((component) => (
						<div key={component.id} className="p-2">
							<strong>{component.name}: </strong>
							{component.description}
						</div>
					))}
				</CustomSection>
			</Conditional>

			<CustomClass />

			<ExecutiveSummary summary={executiveSummary} />

			<CustomVisualInspectionSummary anomalyItems={anomalyItems} />

			<Conditional isVisible={props.utInspectionItems.length > 0}>
				<CustomUTMBreakdown
					summaryTitle='CML Summary'
					displayExpressSummary
					corrosionAllowance={props.inspectionOverview.corrosionAllowance}
					inspectionItems={props.utInspectionItems} 
				/>
			</Conditional>

			<Conditional isVisible={props.concreteHammerData.reading.items.length > 0}>
				<ConcreteHammerSummary 
					startRange={props.concreteHammerData.reading.readingStartRange} 
					endRange={props.concreteHammerData.reading.readingEndRange} 
					data={props.concreteHammerData.reading.items}
				/>
			</Conditional>

			<CustomPDFExport>
				{(params) => (
					<AnomalyRecommendation
						projectDetails={props.projectDetails} 
						items={props.structureItems}
						exportParams={params}
						title='Summary'
						showSummarySection={false}
					/>
				)}
			</CustomPDFExport>

			<StructureAnalytics
				pdoChecklist={[]}
				pidData={props.pidData}
				anomalyLocationData={props.anomalyLocationData}
				checklistItems={props.checklistItems}
				additionalComment={props.additionalComment}
				items={props.structureItems}
				enableComponentInspectionDetails={config.enableNDTComponentInspectionDetails}
			/>

			<Conditional isVisible={!!config.enableNDTComponentInspectionDetails}>
				{props.structureItems.map((item) => (
					<React.Fragment key={item.structureMemberName}>
						<Conditional isVisible={!!item.hammerTestData && item.hammerTestData.reading.items.length > 0}>
							<ConcreteHammerPDF
								title={`Concrete Hammer Test Report - ${item.structureMemberName}`}
								subtitle=""
								isVisible
								item={item.hammerTestData as models.ConcreteHammerData}
							/>
						</Conditional>

						<Conditional isVisible={!!item.utmData && item.utmData.items.length > 0}>
							<CustomUTMReportPDF
								title={`CML Location - ${item.structureMemberName}`}
								tmlLocations={item.utmData?.tmlLocations ?? []}
								utInspectionItems={item.utmData?.items ?? []}
								inspectionOverview={item.utmData?.overview as any}
							/>
						</Conditional>
					</React.Fragment>
				))}
			</Conditional>

			<Conditional isVisible={props.utInspectionItems.length > 0}>
				<CustomUTMReportPDF
					title="CML Location"
					tmlLocations={props.tmlLocations}
					utInspectionItems={props.utInspectionItems}
					inspectionOverview={props.inspectionOverview}
				/>
			</Conditional>

			<Conditional isVisible={props.concreteHammerData.reading.items.length > 0}>
				<ConcreteHammerPDF
					title="Concrete Hammer Test Report"
					subtitle=""
					isVisible 
					item={props.concreteHammerData}
				/>
			</Conditional>

			<Conditional isVisible={props.generalPhotos && props.generalPhotos.length > 0}>
				<CustomGeneralPhotos label="General Photos" photos={props.generalPhotos} />
			</Conditional>
		</React.Fragment>
	);
};

export default withPDF(NDTStructuralReport);
